import React from "react";
import Select, { components } from "react-select";
import Tooltip from "@mui/material/Tooltip";

function GroupedLLMSelector({
  options,
  selectedProviders,
  handleChange,
  disabled = false,
  allowMultipleProviders = false,
}) {
  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    control: (provided) => ({
      ...provided,
      border: "none",
      boxShadow: "none",
      padding: "0",
      minHeight: "24px",
      maxHeight: "24px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 8px",
      margin: "0",
      height: "24px",
      overflowY: "auto",
      display: "flex",
      flexWrap: "nowrap",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0",
      padding: "0",
    }),
    multiValue: (provided) => ({
      ...provided,
      marginTop: "0px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      padding: "0",
      marginTop: "0px",
    }),
    indicatorSeparator: () => ({
      display: "none",
      marginTop: "0px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0",
      marginTop: "0px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0",
      marginTop: "0px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "transparent"
        : provided.backgroundColor,
      color: "black",
      ":hover": {
        backgroundColor: "#f0f0f0",
      },
    }),
  };

  // Custom option component with checkboxes
  const Option = ({ children, ...props }) => {
    return (
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
        />{" "}
        {children}
      </components.Option>
    );
  };

  // Custom MultiValue to show first value and count
  const MultiValue = ({ index, getValue, ...props }) => {
    const selectedValues = getValue();
    if (index === 0) {
      return (
        <components.MultiValue {...props}>
          <Tooltip
            title={`${props?.children} ${
              selectedValues.length > 1 ? `+${selectedValues.length - 1}` : ""
            }`}
          >
            <span>{props?.children} </span>
          </Tooltip>
          {selectedValues.length > 1 && ` +${selectedValues.length - 1}`}
        </components.MultiValue>
      );
    }
    return null;
  };

  // Get unique providers from options
  const providerOptions = React.useMemo(() => {
    if (!options || options.length === 0) return [];

    const providers = {};
    options.forEach((option) => {
      const provider = option.llm_provider || "Other";
      if (!providers[provider]) {
        providers[provider] = {
          label: provider,
          value: provider,
          llms: [],
        };
      }
      providers[provider].llms.push(option);
    });

    return Object.values(providers).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [options]);

  // Get selected values based on selected providers
  const selectedValues = React.useMemo(() => {
    if (!selectedProviders || selectedProviders.length === 0) return [];
    return providerOptions.filter((option) =>
      selectedProviders.includes(option.value)
    );
  }, [providerOptions, selectedProviders]);

  return (
    <Select
      styles={customStyles}
      options={providerOptions}
      closeMenuOnSelect={!allowMultipleProviders}
      hideSelectedOptions={false}
      value={selectedValues}
      components={{
        Option,
        MultiValue,
      }}
      onChange={handleChange}
      className="form-control"
      classNamePrefix="react-select"
      placeholder="LLM Provider"
      isMulti={allowMultipleProviders}
      isDisabled={disabled}
      getOptionLabel={(option) => option?.label}
      getOptionValue={(option) => option?.value}
    />
  );
}

export default GroupedLLMSelector;
